import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit, Trash2, UsersRound, CalendarDays, MapPin, Clock, Trophy, Calendar, Gauge, Loader2, AlertCircle, CheckCircle2, XCircle, Check, X as XIcon, HelpCircle, Bell, BellOff, ChevronDown, Search, UserPlus } from "lucide-react";
import {
  formatGameType,
  formatKickoff,
  formatVenue,
  groupByRound,
  pickLogo,
} from "@/lib/playhq";

const TEAM_TYPES = ["Men", "Women", "Junior Men", "Junior Women"];

const emptyTeam = {
  season: "",
  name: "Derwent CC",
  grade: "",
  format: "",
  type: "Men",
};

function uniqueSorted(arr) {
  return Array.from(new Set(arr.filter((v) => v !== undefined && v !== null && String(v).trim() !== "")))
    .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
}

export default function AdminTeams() {
  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState("");
  const [teams, setTeams] = useState([]);
  // Dialogs
  const [newSeasonOpen, setNewSeasonOpen] = useState(false);
  const [newSeasonName, setNewSeasonName] = useState("");
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTeam);
  const [confirmDel, setConfirmDel] = useState(null); // { kind:"team"|"season", id?, name?, label }
  const [fixtures, setFixtures] = useState([]); // upcoming fixtures for active season

  // PlayHQ fixtures state
  const [phqSeason, setPhqSeason] = useState(null);
  const [phqFixtures, setPhqFixtures] = useState([]);
  const [phqTeamMap, setPhqTeamMap] = useState({});
  const [phqGradeMap, setPhqGradeMap] = useState({});
  const [phqClubTeamIds, setPhqClubTeamIds] = useState(new Set());
  const [phqLoading, setPhqLoading] = useState(true);
  const [phqError, setPhqError] = useState(null);
  const [phqActiveGradeId, setPhqActiveGradeId] = useState("all");

  // Availability activation state
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [activatedGames, setActivatedGames] = useState({}); // { playhq_game_id: fixture_doc }
  const [activating, setActivating] = useState(false);

  // Availability reminder settings
  const [reminderSettings, setReminderSettings] = useState(null);
  const [reminderSaving, setReminderSaving] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);

  // Cross-fixture team selections — { [game_playhq_id]: { [slotKey]: member | null } }
  const [allTeamSlots, setAllTeamSlots] = useState({});
  // PlayHQ selection links (grade → URL)
  const [phqSelectionLinks, setPhqSelectionLinks] = useState({});
  // Track which games have been entered in PlayHQ this session
  const [enteredPlayHQGames, setEnteredPlayHQGames] = useState(new Set());
  const markEnteredPlayHQ = (gameId) => setEnteredPlayHQGames((prev) => new Set(prev).add(gameId));
  const updateGameSlots = (gameId, slotKey, member) => {
    setAllTeamSlots((prev) => ({
      ...prev,
      [gameId]: { ...(prev[gameId] || {}), [slotKey]: member },
    }));
  };

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // ---- loaders ----
  const loadSeasons = async () => {
    try {
      const { data } = await api.get("/seasons");
      setSeasons(data || []);
      if (!activeSeason && data && data.length > 0) {
        setActiveSeason(data[0].name);
      }
    } catch {
      toast.error("Failed to load seasons");
    }
  };

  const loadTeams = async (season) => {
    if (!season) {
      setTeams([]);
      return;
    }
    try {
      const { data } = await api.get(`/teams?season=${encodeURIComponent(season)}`);
      setTeams(data || []);
    } catch {
      toast.error("Failed to load teams");
    }
  };

  const loadFixtures = async (season) => {
    try {
      const { data } = await api.get(`/fixtures?season=${encodeURIComponent(season)}&scope=upcoming`);
      setFixtures(data || []);
    } catch {
      setFixtures([]);
    }
  };

  useEffect(() => { loadSeasons(); }, []);
  useEffect(() => {
    loadTeams(activeSeason);
    if (activeSeason) loadFixtures(activeSeason);
    else setFixtures([]);
  }, [activeSeason]);

  // Load reminder settings
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/availability-reminders");
        setReminderSettings(data);
      } catch {
        setReminderSettings({ enabled: false, reminders: [
          { days_before: 5, time: "09:00", active: true },
          { days_before: 3, time: "09:00", active: false },
          { days_before: 1, time: "18:00", active: false },
        ]});
      }
    })();
  }, []);

  // Load PlayHQ fixtures
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPhqLoading(true);
      setPhqError(null);
      try {
        const seasonsRes = await api.get("/playhq/seasons");
        const seasons = Array.isArray(seasonsRes.data) ? seasonsRes.data : [];
        const preferred = seasons.find((s) => s.status && s.status !== "COMPLETED") || seasons[0];
        if (!preferred) { if (!cancelled) { setPhqSeason(null); setPhqFixtures([]); setPhqLoading(false); } return; }
        if (cancelled) return;
        setPhqSeason(preferred);
        const { data } = await api.get(`/playhq/seasons/${preferred.playhq_id}/club-fixtures`, { params: { status: "UPCOMING" } });
        if (cancelled) return;
        setPhqFixtures(Array.isArray(data?.fixtures) ? data.fixtures : []);
        setPhqTeamMap(data?.team_map || {});
        setPhqGradeMap(data?.grade_map || {});
        setPhqClubTeamIds(new Set(Array.isArray(data?.club_team_ids) ? data.club_team_ids : []));
      } catch (err) {
        if (!cancelled) setPhqError(err?.response?.data?.detail || "Failed to load PlayHQ fixtures");
      } finally {
        if (!cancelled) setPhqLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load which PlayHQ games already have availability activated
  const loadActivatedGames = async () => {
    try {
      const { data } = await api.get("/fixtures/activated-playhq-games");
      setActivatedGames(data || {});
    } catch { /* ignore */ }
  };
  useEffect(() => { loadActivatedGames(); }, []);

  // Load PlayHQ selection links for "Enter team in PlayHQ" button
  useEffect(() => {
    api.get("/settings/selection-links").then(({ data }) => {
      setPhqSelectionLinks(data.links || {});
    }).catch(() => {});
  }, []);

  const handleActivate = async (gameId) => {
    setActivating(true);
    try {
      const { data } = await api.post(`/fixtures/activate-from-playhq/${gameId}`);
      toast.success("Team availability activated for this match");
      setActivatedGames((prev) => ({ ...prev, [gameId]: data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate availability");
    } finally {
      setActivating(false);
    }
  };

  const handleDeactivate = async (gameId) => {
    setActivating(true);
    try {
      await api.delete(`/fixtures/deactivate-playhq/${gameId}`);
      toast.success("Availability deactivated");
      setActivatedGames((prev) => {
        const next = { ...prev };
        delete next[gameId];
        return next;
      });
      // Clear saved team selections for this game
      setAllTeamSlots((prev) => {
        const next = { ...prev };
        delete next[gameId];
        return next;
      });
      setSelectedGameId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not deactivate");
    } finally {
      setActivating(false);
    }
  };

  // ---- suggestions derived from current season's teams ----
  const gradeSuggestions = useMemo(() => uniqueSorted(teams.map((t) => t.grade)), [teams]);
  const formatSuggestions = useMemo(() => uniqueSorted(teams.map((t) => t.format)), [teams]);

  /** Map team_id → upcoming fixtures (max 4 per team) */
  const fixturesByTeam = useMemo(() => {
    const map = {};
    for (const f of fixtures) {
      if (!f.team_id) continue;
      if (!map[f.team_id]) map[f.team_id] = [];
      if (map[f.team_id].length < 4) map[f.team_id].push(f);
    }
    return map;
  }, [fixtures]);

  // PlayHQ derived data
  const shortGradeLabel = (name) => {
    if (!name) return "Grade";
    return name.replace(/^SCA\s+/i, "").replace(/\s+Grade$/i, "").trim() || name;
  };

  const phqGradeChips = useMemo(() => {
    const inUse = new Set(phqFixtures.map((f) => f.grade_id));
    const items = Object.values(phqGradeMap)
      .filter((g) => g && g.id && inUse.has(g.id))
      .map((g) => ({ id: g.id, label: shortGradeLabel(g.name), full: g.name }));
    items.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    return items;
  }, [phqGradeMap, phqFixtures]);

  const phqRounds = useMemo(() => {
    const filtered = phqActiveGradeId === "all"
      ? phqFixtures
      : phqFixtures.filter((f) => f.grade_id === phqActiveGradeId);
    return groupByRound(filtered);
  }, [phqFixtures, phqActiveGradeId]);

  // If active grade chip disappears, fall back to "all"
  useEffect(() => {
    if (phqActiveGradeId !== "all" && !phqGradeChips.some((g) => g.id === phqActiveGradeId)) {
      setPhqActiveGradeId("all");
    }
  }, [phqGradeChips, phqActiveGradeId]);

  // ---- actions ----
  const submitNewSeason = async () => {
    const name = newSeasonName.trim();
    if (!name) { toast.error("Enter a season name (e.g. 2024/25)"); return; }
    try {
      const { data } = await api.post("/seasons", { name });
      toast.success(`Season "${data.name}" ready`);
      setNewSeasonOpen(false);
      setNewSeasonName("");
      await loadSeasons();
      setActiveSeason(data.name);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create season");
    }
  };

  const removeSeason = async (seasonId) => {
    try {
      await api.delete(`/seasons/${seasonId}`);
      toast.success("Season deleted");
      const remaining = seasons.filter((s) => s.id !== seasonId);
      setSeasons(remaining);
      if (remaining.length) setActiveSeason(remaining[0].name);
      else setActiveSeason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete season");
    }
  };

  const startNewTeam = () => {
    if (!activeSeason) { toast.error("Select a season first"); return; }
    setEditing(null);
    setForm({ ...emptyTeam, season: activeSeason });
    setTeamDialogOpen(true);
  };

  const startEditTeam = (t) => {
    setEditing(t);
    setForm({
      season: t.season || activeSeason,
      name: t.name || "Derwent CC",
      grade: t.grade || "",
      format: t.format || "",
      type: t.type || "Men",
    });
    setTeamDialogOpen(true);
  };

  const saveTeam = async () => {
    if (!form.season) { toast.error("Season is required"); return; }
    if (!form.name?.trim()) { toast.error("Team name is required"); return; }
    if (!TEAM_TYPES.includes(form.type)) { toast.error("Pick a valid team type"); return; }
    const payload = {
      season: form.season,
      name: form.name.trim(),
      grade: (form.grade || "").trim(),
      format: (form.format || "").trim(),
      type: form.type,
    };
    try {
      if (editing) await api.put(`/teams/${editing.id}`, payload);
      else await api.post("/teams", payload);
      toast.success(editing ? "Team updated" : "Team created");
      setTeamDialogOpen(false);
      loadTeams(activeSeason);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const removeTeam = async (team) => {
    try {
      await api.delete(`/teams/${team.id}`);
      toast.success("Team deleted");
      loadTeams(activeSeason);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete team");
    }
  };

  const activeSeasonObj = seasons.find((s) => s.name === activeSeason);

  // ---- Reminder settings helpers ----
  const saveReminderSettings = async (newSettings) => {
    setReminderSaving(true);
    try {
      const { data } = await api.put("/settings/availability-reminders", newSettings);
      setReminderSettings({ enabled: data.enabled, reminders: data.reminders });
      toast.success("Reminder settings saved");
    } catch {
      toast.error("Failed to save reminder settings");
    } finally {
      setReminderSaving(false);
    }
  };

  const toggleReminderEnabled = () => {
    if (!reminderSettings) return;
    const next = { ...reminderSettings, enabled: !reminderSettings.enabled };
    setReminderSettings(next);
    saveReminderSettings(next);
  };

  const toggleReminderRow = (idx) => {
    if (!reminderSettings) return;
    const next = { ...reminderSettings, reminders: reminderSettings.reminders.map((r, i) => i === idx ? { ...r, active: !r.active } : r) };
    setReminderSettings(next);
    saveReminderSettings(next);
  };

  const updateReminderTime = (idx, time) => {
    if (!reminderSettings) return;
    const next = { ...reminderSettings, reminders: reminderSettings.reminders.map((r, i) => i === idx ? { ...r, time } : r) };
    setReminderSettings(next);
    saveReminderSettings(next);
  };

  const addReminderRow = () => {
    if (!reminderSettings) return;
    const next = { ...reminderSettings, reminders: [...reminderSettings.reminders, { days_before: 2, time: "09:00", active: true }] };
    setReminderSettings(next);
    saveReminderSettings(next);
  };

  const removeReminderRow = (idx) => {
    if (!reminderSettings) return;
    const next = { ...reminderSettings, reminders: reminderSettings.reminders.filter((_, i) => i !== idx) };
    setReminderSettings(next);
    saveReminderSettings(next);
  };

  const updateReminderDays = (idx, days) => {
    if (!reminderSettings) return;
    const next = { ...reminderSettings, reminders: reminderSettings.reminders.map((r, i) => i === idx ? { ...r, days_before: parseInt(days, 10) } : r) };
    setReminderSettings(next);
    saveReminderSettings(next);
  };

  return (
    <AppShell
      variant="admin"
      title="Teams"
    >
      {/* Teams list */}
      {!activeSeason ? (
        <Card className="p-14 text-center text-muted-foreground border-dashed rounded-2xl">
          <UsersRound className="mx-auto h-8 w-8 mb-3 opacity-60" />
          Create a season to get started.
        </Card>
      ) : teams.length === 0 ? (
        <Card className="p-14 text-center text-muted-foreground border-dashed rounded-2xl">
          <UsersRound className="mx-auto h-8 w-8 mb-3 opacity-60" />
          No teams in <span className="font-medium text-foreground">{activeSeason}</span> yet. Click <span className="font-medium text-foreground">Add team</span> to create one.
        </Card>
      ) : null}

      {/* Availability Reminder Settings */}
      {reminderSettings && (
        <Card className="rounded-2xl border overflow-hidden mb-6" data-testid="teams-reminder-settings">
          <button
            type="button"
            className="w-full px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-muted/30 transition-colors"
            onClick={() => setReminderOpen((o) => !o)}
            data-testid="teams-reminder-header"
          >
            <div className="flex items-center gap-3">
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${reminderSettings.enabled ? "bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]" : "bg-muted text-muted-foreground"}`}>
                {reminderSettings.enabled ? <Bell className="h-4.5 w-4.5" /> : <BellOff className="h-4.5 w-4.5" />}
              </div>
              <div className="text-left">
                <div className="text-sm font-semibold">Availability Reminders</div>
                <div className="text-[11px] text-muted-foreground">Reminder to members with no response</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!reminderOpen && reminderSettings.enabled && (
                <Badge variant="secondary" className="text-[10px] font-medium px-2 py-0.5">
                  {reminderSettings.reminders.filter((r) => r.active).length} active
                </Badge>
              )}
              <ChevronDown className={`h-4.5 w-4.5 text-muted-foreground transition-transform duration-200 ${reminderOpen ? "rotate-180" : ""}`} />
            </div>
          </button>
          {reminderOpen && (
            <div className="border-t border-border/50">
              <div className="px-5 py-3 flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Enable reminders</span>
                <Switch
                  checked={reminderSettings.enabled}
                  onCheckedChange={toggleReminderEnabled}
                  data-testid="teams-reminder-toggle"
                />
              </div>
              {reminderSettings.enabled && (
                <div className="px-5 pb-5 space-y-3">
                  {reminderSettings.reminders.map((r, idx) => (
                <div key={`reminder-${r.days_before}-${r.time}-${idx}`} className="flex items-center gap-3 flex-wrap" data-testid={`teams-reminder-row-${idx}`}>
                  <Switch
                    checked={r.active}
                    onCheckedChange={() => toggleReminderRow(idx)}
                    className="scale-90"
                    data-testid={`teams-reminder-row-toggle-${idx}`}
                  />
                  <Select value={String(r.days_before)} onValueChange={(v) => updateReminderDays(idx, v)}>
                    <SelectTrigger className="h-9 w-[130px] rounded-lg text-xs" data-testid={`teams-reminder-row-days-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days before</SelectItem>
                      <SelectItem value="6">6 days before</SelectItem>
                      <SelectItem value="5">5 days before</SelectItem>
                      <SelectItem value="4">4 days before</SelectItem>
                      <SelectItem value="3">3 days before</SelectItem>
                      <SelectItem value="2">2 days before</SelectItem>
                      <SelectItem value="1">1 day before</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-muted-foreground">at</span>
                  <Select value={r.time} onValueChange={(v) => updateReminderTime(idx, v)}>
                    <SelectTrigger className="h-9 w-[110px] rounded-lg text-xs" data-testid={`teams-reminder-row-time-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="07:00">7:00 AM</SelectItem>
                      <SelectItem value="08:00">8:00 AM</SelectItem>
                      <SelectItem value="09:00">9:00 AM</SelectItem>
                      <SelectItem value="10:00">10:00 AM</SelectItem>
                      <SelectItem value="12:00">12:00 PM</SelectItem>
                      <SelectItem value="15:00">3:00 PM</SelectItem>
                      <SelectItem value="18:00">6:00 PM</SelectItem>
                      <SelectItem value="20:00">8:00 PM</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-red-500"
                    onClick={() => removeReminderRow(idx)}
                    data-testid={`teams-reminder-row-remove-${idx}`}
                  >
                    <XIcon className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs rounded-lg mt-1"
                onClick={addReminderRow}
                data-testid="teams-reminder-add"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add reminder
              </Button>
              {reminderSaving && <span className="text-[10px] text-muted-foreground ml-2">Saving…</span>}
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* PlayHQ Current Season Fixtures */}
      <div className="space-y-5 mt-6" data-testid="teams-phq-fixtures-section">
        {phqLoading ? (
          <Card className="p-10 rounded-2xl text-center text-sm text-muted-foreground" data-testid="teams-phq-loading">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
            <div className="mt-2">Loading PlayHQ fixtures…</div>
          </Card>
        ) : phqError ? (
          <Card className="p-8 rounded-2xl text-center max-w-lg mx-auto" data-testid="teams-phq-error">
            <div className="mx-auto h-12 w-12 rounded-2xl bg-[hsl(var(--secondary))] flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="mt-4 text-base font-semibold tracking-tight">Couldn&apos;t load fixtures</h3>
            <p className="mt-2 text-sm text-muted-foreground">{phqError}</p>
          </Card>
        ) : !phqSeason ? (
          <Card className="p-10 rounded-2xl text-center" data-testid="teams-phq-no-season">
            <div className="mx-auto h-10 w-10 rounded-xl bg-[hsl(var(--secondary))] flex items-center justify-center">
              <Trophy className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="mt-3 text-sm font-semibold">No current season yet</div>
            <p className="mt-1 text-xs text-muted-foreground max-w-xs mx-auto">
              PlayHQ hasn&apos;t published a current season yet. Check back once fixtures are released.
            </p>
          </Card>
        ) : (
          <>
            {/* Season heading */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Current season</div>
              <h2 className="text-lg font-semibold tracking-tight" data-testid="teams-phq-season-name">
                {phqSeason.name}
              </h2>
            </div>

            {/* Grade filter chips */}
            {phqFixtures.length > 0 && phqGradeChips.length > 0 && (
              <div
                className="-mx-1 flex flex-wrap gap-2"
                role="tablist"
                aria-label="Filter fixtures by grade"
                data-testid="teams-phq-grade-chips"
              >
                <PhqGradeChip
                  label="All"
                  testId="teams-phq-grade-chip-all"
                  active={phqActiveGradeId === "all"}
                  onClick={() => setPhqActiveGradeId("all")}
                  count={phqFixtures.length}
                />
                {phqGradeChips.map((g) => (
                  <PhqGradeChip
                    key={g.id}
                    label={g.label}
                    title={g.full}
                    testId={`teams-phq-grade-chip-${g.id}`}
                    active={phqActiveGradeId === g.id}
                    onClick={() => setPhqActiveGradeId(g.id)}
                    count={phqFixtures.filter((f) => f.grade_id === g.id).length}
                  />
                ))}
              </div>
            )}

            {/* Fixture cards */}
            {phqFixtures.length === 0 ? (
              <Card className="p-10 rounded-2xl text-center" data-testid="teams-phq-empty">
                <div className="mx-auto h-10 w-10 rounded-xl bg-[hsl(var(--secondary))] flex items-center justify-center">
                  <Trophy className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="mt-3 text-sm font-semibold">No upcoming fixtures</div>
                <p className="mt-1 text-xs text-muted-foreground max-w-xs mx-auto">
                  Every {phqSeason.name} match for Derwent&apos;s teams has already been played.
                </p>
              </Card>
            ) : phqRounds.length === 0 ? (
              <Card className="p-10 rounded-2xl text-center" data-testid="teams-phq-grade-empty">
                <div className="mx-auto h-10 w-10 rounded-xl bg-[hsl(var(--secondary))] flex items-center justify-center">
                  <Trophy className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="mt-3 text-sm font-semibold">No matches for this grade</div>
                <p className="mt-1 text-xs text-muted-foreground max-w-xs mx-auto">
                  Try another grade, or tap <span className="font-medium text-foreground">All</span> to see everything upcoming.
                </p>
              </Card>
            ) : (
              <div className="space-y-6" data-testid="teams-phq-rounds">
                {phqRounds.map((round) => {
                  // Count activated games with a configured selection link for this round
                  const activatedWithLink = round.games.filter((g) => {
                    if (!activatedGames[g.playhq_id]) return false;
                    const gn = phqGradeMap[g.grade_id]?.name;
                    const ct = (g.teams || []).find((t) => phqClubTeamIds.has(t.id));
                    const ctn = ct ? (phqTeamMap[ct.id]?.name || ct.name || "") : "";
                    return !!(phqSelectionLinks[gn] || phqSelectionLinks[ctn]);
                  });
                  const enteredCount = activatedWithLink.filter((g) => enteredPlayHQGames.has(g.playhq_id)).length;
                  const totalLinkable = activatedWithLink.length;

                  return (
                  <div key={round.name} data-testid={`teams-phq-round-${round.name.replace(/\s+/g, "-")}`}>
                    <div className="flex items-baseline justify-between mb-2 px-1">
                      <h3 className="text-sm font-semibold tracking-tight text-foreground/80">
                        {round.name}
                      </h3>
                      <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                        {round.games.length} game{round.games.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    {totalLinkable > 0 && (
                      <div className="flex items-center gap-2.5 mb-3 px-1" data-testid={`teams-round-progress-${round.name.replace(/\s+/g, "-")}`}>
                        <div className="flex-1 h-1.5 rounded-full bg-border/60 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                            style={{ width: `${totalLinkable > 0 ? (enteredCount / totalLinkable) * 100 : 0}%` }}
                          />
                        </div>
                        <span className={`text-[11px] font-medium tabular-nums whitespace-nowrap ${enteredCount === totalLinkable ? "text-emerald-600" : "text-muted-foreground"}`}>
                          {enteredCount}/{totalLinkable} PlayHQ
                        </span>
                      </div>
                    )}
                    <ul className="space-y-3">
                      {round.games.map((game) => (
                        <PhqFixtureRow
                          key={game.playhq_id}
                          game={game}
                          teamMap={phqTeamMap}
                          gradeMap={phqGradeMap}
                          clubTeamIds={phqClubTeamIds}
                          isSelected={selectedGameId === game.playhq_id}
                          onSelect={() => setSelectedGameId(selectedGameId === game.playhq_id ? null : game.playhq_id)}
                          activated={activatedGames[game.playhq_id] || null}
                          onActivate={() => handleActivate(game.playhq_id)}
                          onDeactivate={() => handleDeactivate(game.playhq_id)}
                          activating={activating}
                          allTeamSlots={allTeamSlots}
                          updateGameSlots={updateGameSlots}
                          selectionLinks={phqSelectionLinks}
                          enteredPlayHQ={enteredPlayHQGames.has(game.playhq_id)}
                          onEnteredPlayHQ={() => markEnteredPlayHQ(game.playhq_id)}
                        />
                      ))}
                    </ul>
                  </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add season dialog */}
      <Dialog open={newSeasonOpen} onOpenChange={setNewSeasonOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle>Add season</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium" htmlFor="new-season-name">Season name</Label>
              <Input
                id="new-season-name"
                value={newSeasonName}
                onChange={(e) => setNewSeasonName(e.target.value)}
                placeholder="e.g. 2024/25"
                className="mt-1.5 h-11 rounded-lg"
                data-testid="teams-new-season-input"
                autoFocus
              />
            </div>
            <Button onClick={submitNewSeason} className="w-full h-11 btn-primary-modern" data-testid="teams-new-season-save">
              Save season
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Team create/edit dialog */}
      <Dialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit team" : "New team"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground">Season: <span className="font-medium text-foreground">{form.season}</span></div>
            <div>
              <Label className="text-sm font-medium">Team name</Label>
              <Input
                value={form.name}
                onChange={(e) => setF("name", e.target.value)}
                placeholder="Derwent CC"
                className="mt-1.5 h-11 rounded-lg"
                data-testid="team-form-name"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Grade</Label>
                <Input
                  value={form.grade}
                  onChange={(e) => setF("grade", e.target.value)}
                  placeholder="e.g. 1"
                  list="team-grade-suggestions"
                  inputMode="numeric"
                  className="mt-1.5 h-11 rounded-lg"
                  data-testid="team-form-grade"
                />
                <datalist id="team-grade-suggestions">
                  {gradeSuggestions.map((g) => <option key={g} value={g} />)}
                </datalist>
              </div>
              <div>
                <Label className="text-sm font-medium">Format</Label>
                <Input
                  value={form.format}
                  onChange={(e) => setF("format", e.target.value)}
                  placeholder="e.g. Two-Day"
                  list="team-format-suggestions"
                  className="mt-1.5 h-11 rounded-lg"
                  data-testid="team-form-format"
                />
                <datalist id="team-format-suggestions">
                  {formatSuggestions.map((g) => <option key={g} value={g} />)}
                </datalist>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Type</Label>
              <Select value={form.type} onValueChange={(v) => setF("type", v)}>
                <SelectTrigger className="mt-1.5 h-11 rounded-lg" data-testid="team-form-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEAM_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`team-form-type-option-${t.replace(/\s+/g, "-").toLowerCase()}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={saveTeam} className="w-full h-11 btn-primary-modern" data-testid="team-form-save">
              {editing ? "Save changes" : "Create team"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete confirmation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-medium text-foreground">{confirmDel?.label}</span>? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="teams-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="teams-confirm-delete"
              onClick={async () => {
                const c = confirmDel;
                setConfirmDel(null);
                if (!c) return;
                if (c.kind === "season") await removeSeason(c.id);
                else if (c.kind === "team") await removeTeam(c.team);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// PlayHQ fixture row — mirrors the member Fixtures page layout
// ---------------------------------------------------------------------------
function PhqFixtureRow({ game, teamMap, gradeMap, clubTeamIds, isSelected, onSelect, activated, onActivate, onDeactivate, activating, allTeamSlots, updateGameSlots, selectionLinks, enteredPlayHQ, onEnteredPlayHQ }) {
  const home = (game.teams || []).find((t) => t.isHomeTeam) || (game.teams || [])[0] || {};
  const away = (game.teams || []).find((t) => !t.isHomeTeam) || (game.teams || [])[1] || {};
  const involvesClub = (game.teams || []).some((t) => clubTeamIds.has(t.id));
  // Detect intra-club match (both teams are Derwent)
  const isBothClub = (game.teams || []).filter((t) => clubTeamIds.has(t.id)).length >= 2;

  const kickoff = formatKickoff(game.start_at);
  const venueLabel = formatVenue(game.venue);
  const format = formatGameType(game.type);
  const gradeName = gradeMap[game.grade_id]?.name;
  // Derive club team name for PlayHQ selection link lookup (e.g. "Derwent Women CC White")
  const clubTeamName = (() => {
    const ct = (game.teams || []).find((t) => clubTeamIds.has(t.id));
    return ct ? (teamMap[ct.id]?.name || ct.name || "") : "";
  })();
  const isActivated = !!activated;

  // For intra-club: track which side is being managed
  const [selectedSide, setSelectedSide] = useState(null); // "home" | "away" | null

  // Derive team names for intra-club display
  const homeTeamName = teamMap[home.id]?.name || home.name || "Home";
  const awayTeamName = teamMap[away.id]?.name || away.name || "Away";

  // Compute effective game ID for API calls (suffixed for intra-club)
  const effectiveGameId = isBothClub && selectedSide
    ? `${game.playhq_id}__${selectedSide}`
    : game.playhq_id;

  // For intra-club: derive club team name based on selected side
  const effectiveClubTeamName = isBothClub && selectedSide
    ? (selectedSide === "home" ? homeTeamName : awayTeamName)
    : clubTeamName;

  // Derive opposition team name for highlighting historical matches
  const opponentTeam = (() => {
    const teams = game.teams || [];
    if (isBothClub && selectedSide) {
      // In intra-club, the opponent is the other Derwent team
      return selectedSide === "home" ? awayTeamName : homeTeamName;
    }
    const opp = teams.find((t) => !clubTeamIds.has(t.id));
    return opp ? (teamMap[opp.id]?.name || opp.name || "") : "";
  })();

  const [showAvail, setShowAvail] = useState(false);
  const [availData, setAvailData] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [availFilter, setAvailFilter] = useState(null); // null = all, "available" | "reserve" | "unavailable"
  const [showTeamSelect, setShowTeamSelect] = useState(false);
  const [teamSlots, setTeamSlots] = useState(() => {
    const slots = {};
    for (let i = 1; i <= 11; i++) slots[`pos-${i}`] = null;
    for (let i = 1; i <= 5; i++) slots[`emg-${i}`] = null;
    return slots;
  });
  const [teamRoles, setTeamRoles] = useState({}); // { "pos-1": "C", "pos-3": "VC" }
  // Sync local teamSlots changes up to the parent shared state
  const setTeamSlotsAndSync = (updater) => {
    setTeamSlots((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      // Sync each changed slot to parent — use effectiveGameId for intra-club
      const syncId = isBothClub && selectedSide ? effectiveGameId : game.playhq_id;
      Object.keys(next).forEach((slotKey) => {
        if (next[slotKey] !== prev[slotKey]) {
          updateGameSlots(syncId, slotKey, next[slotKey]);
        }
      });
      return next;
    });
  };
  const [activeSlot, setActiveSlot] = useState(null); // currently selected slot key
  const [battingRanks, setBattingRanks] = useState({}); // { position: { player_id: {total_runs, innings, avg, matches} } }
  const [keeperRanks, setKeeperRanks] = useState(null);
  const [bowlingRanks, setBowlingRanks] = useState(null);
  const [statsOpen, setStatsOpen] = useState(null);
  const [rankLoading, setRankLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [unpublishConfirmOpen, setUnpublishConfirmOpen] = useState(false);
  const [deliveryLog, setDeliveryLog] = useState(null);
  const [notifyNonSelected, setNotifyNonSelected] = useState(false);
  const [enterPlayerOpen, setEnterPlayerOpen] = useState(false);
  const [enterPlayerSearch, setEnterPlayerSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimer = useRef(null);

  // Load saved team selection from backend on mount or when side changes
  useEffect(() => {
    if (!activated) return;
    // For intra-club, only load when a side is selected
    if (isBothClub && !selectedSide) return;
    (async () => {
      try {
        const { data } = await api.get(`/fixtures/team-selection/${effectiveGameId}`);
        const saved = data.slots || {};
        const savedRoles = data.roles || {};
        // Reset slots first for intra-club side switches
        const freshSlots = {};
        for (let i = 1; i <= 11; i++) freshSlots[`pos-${i}`] = null;
        for (let i = 1; i <= 5; i++) freshSlots[`emg-${i}`] = null;
        if (Object.values(saved).some(Boolean)) {
          Object.entries(saved).forEach(([k, v]) => { freshSlots[k] = v || null; });
        }
        setTeamSlotsAndSync(() => freshSlots);
        setTeamRoles(Object.keys(savedRoles).length > 0 ? savedRoles : {});
        setIsPublished(!!data.published);
      } catch {}
    })();
  }, [activated?.id, effectiveGameId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save team selection to backend
  const handleSaveTeam = async () => {
    setSaving(true);
    try {
      await api.put(`/fixtures/team-selection/${effectiveGameId}`, { slots: teamSlots, roles: teamRoles });
      const { toast } = await import("sonner");
      toast.success("Team saved");
    } catch {
      const { toast } = await import("sonner");
      toast.error("Failed to save team");
    }
    setSaving(false);
  };

  // Publish team — saves first, then publishes + sends notifications
  const handlePublishTeam = async () => {
    const filledSlots = Object.values(teamSlots).filter(Boolean);
    if (filledSlots.length === 0) {
      toast.error("Select at least one player before publishing");
      return;
    }
    setPublishing(true);
    try {
      // Save first to ensure latest draft is persisted
      await api.put(`/fixtures/team-selection/${effectiveGameId}`, { slots: teamSlots, roles: teamRoles });
      // Then publish (with optional non-selection notice)
      const { data } = await api.post(`/fixtures/publish-team/${effectiveGameId}?notify_non_selected=${notifyNonSelected}`);
      if (data.flyer_news_id) {
        toast.success("Team published — flyer added to News");
      } else {
        toast.success("Team published");
        if (data.flyer_error) {
          toast.error(`Flyer not generated: ${data.flyer_error}`, { duration: 8000 });
        }
      }
      setIsPublished(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to publish team");
    }
    setPublishing(false);
  };

  // Unpublish team — retracts the selection and removes the flyer news
  const handleUnpublishTeam = async () => {
    setUnpublishing(true);
    try {
      const { data } = await api.post(`/fixtures/unpublish-team/${effectiveGameId}`);
      toast.success("Team unpublished — flyer removed from News");
      setIsPublished(false);
      setUnpublishConfirmOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unpublish team");
    }
    setUnpublishing(false);
  };

  const loadAvailability = async () => {
    if (!activated?.id) return;
    setAvailLoading(true);
    try {
      const { data } = await api.get(`/fixtures/${activated.id}/availability/all`);
      setAvailData(data);
    } catch { setAvailData(null); }
    setAvailLoading(false);
  };

  // Match type for filtering stats (oneDay or t20)
  const matchType = game.type || "";

  // Fetch batting rank for a specific position (cached per position+matchType)
  const loadBattingRank = async (pos) => {
    const cacheKey = `${pos}_${matchType}`;
    if (battingRanks[cacheKey]) return;
    setRankLoading(true);
    try {
      const { data } = await api.get(`/stats/batting-rank?position=${pos}&match_type=${matchType}`);
      setBattingRanks((prev) => ({ ...prev, [cacheKey]: data.rankings || {} }));
    } catch {}
    setRankLoading(false);
  };

  // Fetch keeper rank (cached once per matchType)
  const loadKeeperRank = async () => {
    if (keeperRanks) return;
    setRankLoading(true);
    try {
      const { data } = await api.get(`/stats/keeper-rank?match_type=${matchType}`);
      setKeeperRanks(data.rankings || {});
    } catch {}
    setRankLoading(false);
  };

  // Fetch bowling rank (cached once per matchType)
  const loadBowlingRank = async () => {
    if (bowlingRanks) return;
    setRankLoading(true);
    try {
      const { data } = await api.get(`/stats/bowling-rank?match_type=${matchType}`);
      setBowlingRanks(data.rankings || {});
    } catch {}
    setRankLoading(false);
  };

  // Auto-load availability counts when fixture is activated
  React.useEffect(() => {
    if (isActivated && !availData && !availLoading) {
      loadAvailability();
    }
  }, [isActivated]);

  // Collapse team selection card when deselected
  React.useEffect(() => {
    if (!isSelected) {
      setShowTeamSelect(false);
    }
  }, [isSelected]);

  return (
    <li
      className={`rounded-2xl border overflow-hidden cursor-pointer transition-shadow ${
        isSelected
          ? "ring-2 ring-[hsl(var(--accent))] shadow-md"
          : ""
      } ${
        involvesClub
          ? "border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/[0.04]"
          : "border-border bg-[hsl(var(--card))]"
      }`}
      data-testid={`teams-phq-card-${game.playhq_id}`}
      onClick={onSelect}
    >
      <div className="flex items-center justify-end gap-1.5 px-4 pt-3">
        {isActivated && (
          <Badge
            variant="outline"
            className="text-[10px] font-semibold uppercase tracking-wider bg-emerald-500/10 text-emerald-600 border-emerald-500/30"
            data-testid={`teams-phq-avail-badge-${game.playhq_id}`}
          >
            Availability Active
          </Badge>
        )}
        <Badge
          variant="outline"
          className="text-[10px] font-semibold uppercase tracking-wider bg-blue-500/10 text-blue-600 border-blue-500/30"
          data-testid={`teams-phq-status-${game.playhq_id}`}
        >
          Upcoming
        </Badge>
      </div>

      <div className="px-4 pb-4 pt-1 grid grid-cols-[1fr_auto_auto_1fr] items-center gap-x-2 sm:gap-x-4">
        <PhqFixtureTeam teamRef={home} teamMap={teamMap} clubTeamIds={clubTeamIds} align="right" />
        <div className="text-center text-xl sm:text-2xl font-mono font-medium text-muted-foreground/60">vs</div>
        <div />
        <PhqFixtureTeam teamRef={away} teamMap={teamMap} clubTeamIds={clubTeamIds} align="left" />
      </div>

      <div className="border-t border-border/60 bg-[hsl(var(--bg))]/40 px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
        {format && (
          <span className="inline-flex items-center gap-1.5" data-testid={`teams-phq-format-${game.playhq_id}`}>
            <Gauge className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
            <span className="text-foreground/70">{format}</span>
          </span>
        )}
        <span className="inline-flex items-center gap-1.5" data-testid={`teams-phq-kickoff-${game.playhq_id}`}>
          <Calendar className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
          <span className="text-foreground/70">{kickoff}</span>
        </span>
        {venueLabel && (
          <span className="inline-flex items-center gap-1.5" data-testid={`teams-phq-venue-${game.playhq_id}`}>
            <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
            <span className="text-foreground/70">{venueLabel}</span>
          </span>
        )}
        {gradeName && (
          <span className="inline-flex items-center gap-1.5" data-testid={`teams-phq-grade-${game.playhq_id}`}>
            <Trophy className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
            <span className="text-foreground/70">{gradeName}</span>
          </span>
        )}
      </div>

      {/* Activated fixture — always show counts + action buttons */}
      {isActivated && (
        <div
          className="border-t border-border/60 px-4 py-3 bg-[hsl(var(--secondary))]/30"
          onClick={(e) => e.stopPropagation()}
          data-testid={`teams-phq-activated-bar-${game.playhq_id}`}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            {availData && !availLoading ? (
              <div className="flex items-center gap-3 flex-wrap text-xs font-semibold">
                <button
                  type="button"
                  className={`inline-flex items-center gap-1.5 cursor-pointer rounded-md px-2 py-1 transition-colors ${availFilter === "available" ? "bg-emerald-500/15 ring-1 ring-emerald-500/40" : "hover:bg-emerald-500/10"} text-emerald-600`}
                  onClick={(e) => { e.stopPropagation(); const on = availFilter !== "available"; setAvailFilter(on ? "available" : null); if (on) { setShowAvail(true); loadAvailability(); } }}
                  data-testid={`teams-phq-filter-available-${game.playhq_id}`}
                >
                  <Check className="h-3.5 w-3.5" /> {availData.counts?.available || 0} Available
                </button>
                <button
                  type="button"
                  className={`inline-flex items-center gap-1.5 cursor-pointer rounded-md px-2 py-1 transition-colors ${availFilter === "reserve" ? "bg-amber-500/15 ring-1 ring-amber-500/40" : "hover:bg-amber-500/10"} text-amber-600`}
                  onClick={(e) => { e.stopPropagation(); const on = availFilter !== "reserve"; setAvailFilter(on ? "reserve" : null); if (on) { setShowAvail(true); loadAvailability(); } }}
                  data-testid={`teams-phq-filter-reserve-${game.playhq_id}`}
                >
                  <Clock className="h-3.5 w-3.5" /> {availData.counts?.reserve || 0} Reserve
                </button>
                <button
                  type="button"
                  className={`inline-flex items-center gap-1.5 cursor-pointer rounded-md px-2 py-1 transition-colors ${availFilter === "unavailable" ? "bg-red-500/15 ring-1 ring-red-500/40" : "hover:bg-red-500/10"} text-red-500`}
                  onClick={(e) => { e.stopPropagation(); const on = availFilter !== "unavailable"; setAvailFilter(on ? "unavailable" : null); if (on) { setShowAvail(true); loadAvailability(); } }}
                  data-testid={`teams-phq-filter-unavailable-${game.playhq_id}`}
                >
                  <XIcon className="h-3.5 w-3.5" /> {availData.counts?.unavailable || 0} Unavailable
                </button>
              </div>
            ) : availLoading ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</span>
            ) : <div />}
            <div className="flex items-center gap-2">
              {isBothClub ? (
                <>
                  <Button
                    size="sm"
                    className={`text-xs ${selectedSide === "home" && showTeamSelect ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "btn-primary-modern"}`}
                    onClick={() => {
                      const isCurrentSide = selectedSide === "home" && showTeamSelect;
                      if (isCurrentSide) {
                        setShowTeamSelect(false);
                        setSelectedSide(null);
                      } else {
                        setSelectedSide("home");
                        loadAvailability();
                        setShowTeamSelect(true);
                        setShowAvail(false);
                      }
                    }}
                    data-testid={`teams-phq-select-home-${game.playhq_id}`}
                  >
                    <UsersRound className="h-3.5 w-3.5 mr-1.5" />
                    {selectedSide === "home" && showTeamSelect ? "Hide" : "Select"} {homeTeamName.replace(/^Derwent\s*/i, "").replace(/\s*CC\s*/i, " ").trim() || "Home"}
                  </Button>
                  <Button
                    size="sm"
                    className={`text-xs ${selectedSide === "away" && showTeamSelect ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "btn-primary-modern"}`}
                    onClick={() => {
                      const isCurrentSide = selectedSide === "away" && showTeamSelect;
                      if (isCurrentSide) {
                        setShowTeamSelect(false);
                        setSelectedSide(null);
                      } else {
                        setSelectedSide("away");
                        loadAvailability();
                        setShowTeamSelect(true);
                        setShowAvail(false);
                      }
                    }}
                    data-testid={`teams-phq-select-away-${game.playhq_id}`}
                  >
                    <UsersRound className="h-3.5 w-3.5 mr-1.5" />
                    {selectedSide === "away" && showTeamSelect ? "Hide" : "Select"} {awayTeamName.replace(/^Derwent\s*/i, "").replace(/\s*CC\s*/i, " ").trim() || "Away"}
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  className="btn-primary-modern text-xs"
                  onClick={() => {
                    if (showTeamSelect) { setShowTeamSelect(false); } else { loadAvailability(); setShowTeamSelect(true); setShowAvail(false); }
                  }}
                  data-testid={`teams-phq-select-team-${game.playhq_id}`}
                >
                  <UsersRound className="h-3.5 w-3.5 mr-1.5" />
                  {showTeamSelect ? "Hide Selection" : "Select Team"}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="text-xs border-destructive/40 text-destructive hover:bg-destructive/10"
                onClick={() => onDeactivate()}
                disabled={activating}
                data-testid={`teams-phq-deactivate-${game.playhq_id}`}
              >
                {activating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <XCircle className="h-3.5 w-3.5 mr-1.5" />}
                Deactivate
              </Button>
            </div>
          </div>
          {/* Team Selection Card */}
          {showTeamSelect && (
            <div className="mt-3 rounded-xl border border-border/60 bg-background/60 overflow-hidden" data-testid={`teams-phq-selection-card-${game.playhq_id}`} onClick={(e) => e.stopPropagation()}>
              {availLoading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : !availData ? (
                <div className="text-sm text-muted-foreground text-center py-6">Could not load availability.</div>
              ) : (() => {
                const assignedIds = new Set(Object.values(teamSlots).filter(Boolean).map((m) => m.user_id));
                const isEmergencySlot = activeSlot && activeSlot.startsWith("emg-");
                // Build set of player IDs assigned to non-emergency slots in OTHER fixtures
                const otherFixtureAssigned = new Set();
                if (!isEmergencySlot) {
                  Object.entries(allTeamSlots || {}).forEach(([gId, slots]) => {
                    if (gId === game.playhq_id) return; // skip this fixture
                    // For intra-club: also skip current side but include other side
                    if (isBothClub && gId === effectiveGameId) return;
                    Object.entries(slots || {}).forEach(([sk, member]) => {
                      if (member && sk.startsWith("pos-")) {
                        otherFixtureAssigned.add(member.user_id);
                      }
                    });
                  });
                }
                const pool = (availData.rows || []).filter((r) => (r.status === "available" || r.status === "reserve") && !assignedIds.has(r.user_id) && !otherFixtureAssigned.has(r.user_id));
                const assignToSlot = (slotKey, member) => { setTeamSlotsAndSync((prev) => ({ ...prev, [slotKey]: member })); setActiveSlot(null); };
                const removeFromSlot = (slotKey) => {
                  setTeamSlotsAndSync((prev) => ({ ...prev, [slotKey]: null }));
                  setTeamRoles((prev) => { const next = { ...prev }; delete next[slotKey]; return next; });
                };
                const posSlots = Array.from({ length: 11 }, (_, i) => `pos-${i + 1}`);
                const emgSlots = Array.from({ length: 5 }, (_, i) => `emg-${i + 1}`);

                // Determine stat sort key based on active slot
                const slotIdx = activeSlot ? parseInt(activeSlot.replace("pos-","").replace("emg-",""), 10) : null;
                const isBatterSlot = activeSlot && activeSlot.startsWith("pos-") && slotIdx >= 1 && slotIdx <= 5;
                const isKeeperSlot = activeSlot && activeSlot === "pos-6";
                const isBowlerSlot = activeSlot && activeSlot.startsWith("pos-") && slotIdx >= 7 && slotIdx <= 11;

                // Resolve the correct ranks object based on active slot type
                const activePos = activeSlot ? parseInt(activeSlot.replace("pos-", "").replace("emg-", ""), 10) : null;
                const ranks = isBatterSlot && activePos && battingRanks[`${activePos}_${matchType}`]
                  ? battingRanks[`${activePos}_${matchType}`]
                  : isKeeperSlot && keeperRanks
                    ? keeperRanks
                    : isBowlerSlot && bowlingRanks
                      ? bowlingRanks
                      : {};

                // Sort pool based on slot type
                const sortedPool = [...pool];
                if (activeSlot && Object.keys(ranks).length > 0) {
                  if (isBatterSlot) {
                    // Sort by runs descending, then average
                    sortedPool.sort((a, b) => {
                      const ra = ranks[a.user_id]?.total_runs || 0;
                      const rb = ranks[b.user_id]?.total_runs || 0;
                      if (rb !== ra) return rb - ra;
                      return (ranks[b.user_id]?.avg || 0) - (ranks[a.user_id]?.avg || 0);
                    });
                  } else if (isKeeperSlot) {
                    // Sort by dismissals descending, then last5 runs
                    sortedPool.sort((a, b) => {
                      const da = ranks[a.user_id]?.dismissals || 0;
                      const db = ranks[b.user_id]?.dismissals || 0;
                      if (db !== da) return db - da;
                      return (ranks[b.user_id]?.last5_runs || 0) - (ranks[a.user_id]?.last5_runs || 0);
                    });
                  } else if (isBowlerSlot) {
                    // Sort by wickets descending, then bowling avg ascending
                    sortedPool.sort((a, b) => {
                      const wa = ranks[a.user_id]?.wickets || 0;
                      const wb = ranks[b.user_id]?.wickets || 0;
                      if (wb !== wa) return wb - wa;
                      const aa = ranks[a.user_id]?.bowling_avg ?? 9999;
                      const ab = ranks[b.user_id]?.bowling_avg ?? 9999;
                      return aa - ab;
                    });
                  }
                }

                const posLabel = (idx) => idx < 5 ? "Batter" : idx === 5 ? "Wicket Keeper" : "Bowler";
                const handleSlotClick = (slotKey, idx) => {
                  if (teamSlots[slotKey]) return; // already filled
                  const isActive = activeSlot === slotKey;
                  if (isActive) { setActiveSlot(null); setStatsOpen(null); return; }
                  setActiveSlot(slotKey);
                  setStatsOpen(null);
                  const pos = idx + 1;
                  if (pos >= 1 && pos <= 5) {
                    loadBattingRank(pos);
                  } else if (pos === 6) {
                    loadKeeperRank();
                  } else if (pos >= 7 && pos <= 11) {
                    loadBowlingRank();
                  }
                };

                // Header subtitle based on slot type
                const headerSubtitle = isBatterSlot
                  ? ` · pos ${activePos} stats`
                  : isKeeperSlot
                    ? " · keeper stats"
                    : isBowlerSlot
                      ? " · bowling stats (last 5)"
                      : "";
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border/60">
                    {/* LEFT: Available & Reserve members pool */}
                    <div className="p-4">
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                        {activeSlot ? `Pick for ${posLabel(parseInt(activeSlot.replace("pos-","").replace("emg-",""), 10) - 1)}` : `Available & Reserve (${pool.length})`}
                        {activeSlot && Object.keys(ranks).length > 0 && <span className="ml-1 text-[10px] font-normal normal-case">{headerSubtitle}</span>}
                        {activeSlot && rankLoading && <span className="ml-1 text-[10px] font-normal normal-case"> · loading…</span>}
                      </div>
                      <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
                        {sortedPool.length === 0 && (
                          <div className="text-xs text-muted-foreground text-center py-4">All members assigned</div>
                        )}
                        {sortedPool.map((row) => {
                          const targetSlot = activeSlot || posSlots.find((k) => !teamSlots[k]) || emgSlots.find((k) => !teamSlots[k]);
                          const playerRank = ranks[row.user_id];
                          const isStatsOpen = statsOpen === row.user_id;
                          // Check if any historical match is vs the current fixture opponent (club-level)
                          const stripGradePlayer = (n) => (n || "").replace(/\s+\d+(st|nd|rd|th)?\s*(grade)?$/i, "").replace(/\s+\d+$/, "").trim();
                          const stripClubNum = (n) => (n || "").replace(/\s*CC\s*/, " ").replace(/\s+\d+$/, "").trim();
                          const hasVsOpponent = (() => {
                            if (!opponentTeam || !playerRank) return false;
                            const allMatches = [
                              ...(playerRank.matches || []),
                              ...(playerRank.keeper_matches || []),
                              ...(playerRank.last5_matches || []),
                            ];
                            return allMatches.some((m) => m.opponent && stripGradePlayer(m.opponent) === stripGradePlayer(opponentTeam));
                          })();
                          const vsClubName = hasVsOpponent ? stripClubNum(opponentTeam) : "";
                          return (
                            <div key={row.user_id} className="space-y-0">
                              <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-[hsl(var(--secondary))]/60 transition-colors">
                                {row.avatar_url ? (
                                  <img src={row.avatar_url} alt="" className="h-7 w-7 rounded-full object-cover bg-[hsl(var(--secondary))] shrink-0" />
                                ) : (
                                  <div className="h-7 w-7 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-[10px] font-bold text-muted-foreground shrink-0">
                                    {(row.first_name?.[0] || "")}{(row.last_name?.[0] || "")}
                                  </div>
                                )}
                                <button
                                  type="button"
                                  className="flex-1 min-w-0 text-left cursor-pointer"
                                  onClick={(e) => { e.stopPropagation(); setStatsOpen(isStatsOpen ? null : row.user_id); }}
                                  data-testid={`teams-pool-name-${row.user_id}`}
                                >
                                  <div className="text-sm font-medium truncate flex items-center gap-1.5">{row.first_name} {row.last_name}{vsClubName && <span className="text-[9px] font-medium bg-emerald-500/15 text-emerald-600 px-1.5 py-0.5 rounded shrink-0">vs {vsClubName}</span>}</div>
                                  {/* Batter summary */}
                                  {isBatterSlot && playerRank && (
                                    <div className="text-[10px] text-muted-foreground mt-0.5">
                                      <span className="font-semibold text-[hsl(var(--accent))]">{playerRank.total_runs}</span> runs · <span className="font-semibold text-[hsl(var(--accent))]">{playerRank.avg}</span> avg <span className="text-muted-foreground/60">({playerRank.innings} inn)</span>
                                    </div>
                                  )}
                                  {/* Keeper summary */}
                                  {isKeeperSlot && playerRank && (
                                    <div className="text-[10px] text-muted-foreground mt-0.5">
                                      <span className="font-semibold text-[hsl(var(--accent))]">{playerRank.dismissals}</span> dismissals <span className="text-muted-foreground/60">({playerRank.catches}c {playerRank.stumpings}st)</span> · <span className="font-semibold text-[hsl(var(--accent))]">{playerRank.last5_runs}</span> runs <span className="text-muted-foreground/60">(last 5)</span>
                                    </div>
                                  )}
                                  {/* Bowler summary */}
                                  {isBowlerSlot && playerRank && (
                                    <div className="text-[10px] text-muted-foreground mt-0.5">
                                      <span className="font-semibold text-[hsl(var(--accent))]">{playerRank.wickets}</span> wkts · avg <span className="font-semibold text-[hsl(var(--accent))]">{playerRank.bowling_avg ?? "–"}</span> · SR <span className="font-semibold text-[hsl(var(--accent))]">{playerRank.bowling_sr ?? "–"}</span> <span className="text-muted-foreground/60">(last {playerRank.innings})</span>
                                    </div>
                                  )}
                                </button>
                                {row.is_guest ? (
                                  <Badge variant="outline" className="text-[9px] font-semibold bg-amber-500/10 text-amber-600 border-amber-500/30 shrink-0">Guest</Badge>
                                ) : row.status === "reserve" ? (
                                  <Badge variant="outline" className="text-[9px] font-semibold bg-amber-500/10 text-amber-600 border-amber-500/30 shrink-0">Reserve</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-[9px] font-semibold bg-emerald-500/10 text-emerald-600 border-emerald-500/30 shrink-0">Available</Badge>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-[10px] shrink-0"
                                  onClick={() => targetSlot && assignToSlot(targetSlot, row)}
                                  disabled={!targetSlot}
                                  data-testid={`teams-pool-assign-${row.user_id}`}
                                >+</Button>
                              </div>
                              {isStatsOpen && playerRank && (() => {
                                // Determine which matches array to show based on slot type
                                const matchList = (isBatterSlot
                                  ? (playerRank.matches || [])
                                  : isKeeperSlot
                                    ? (playerRank.keeper_matches || [])
                                    : isBowlerSlot
                                      ? (playerRank.matches || [])
                                      : []).slice(0, 5);
                                if (matchList.length === 0) return null;
                                return (
                                  <div className="mr-2 mb-1.5 ml-3 p-3 rounded-lg bg-[hsl(var(--secondary))]/40 border border-border/40 space-y-1" data-testid={`teams-pool-stats-${row.user_id}`}>
                                    {matchList.map((m, mi) => {
                                      const matchKey = `${m.season || ""}-${m.round || ""}-${m.date || ""}-${m.opponent || ""}-${mi}`;
                                      const rnd = m.round ? m.round.replace("Round ", "Rnd ").replace("Preliminary Final", "PF").replace("Grand Final", "GF").replace(/Finals Rnd (\d+)/i, "FR $1").replace(/Finals Round (\d+)/i, "FR $1") : "";
                                      const d = m.date ? new Date(m.date) : null;
                                      const dateStr = d ? d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "2-digit" }) : "";
                                      const gradeClean = (m.grade || "").replace("KFC SCA ", "").replace("KFC ", "");
                                      const metaParts = [(m.season || "").replace(/^20/, ""), gradeClean, rnd, dateStr].filter(Boolean).join(" · ");
                                      // Match opponent at club level (strip trailing grade number)
                                      const stripGrade = (name) => (name || "").replace(/\s+\d+(st|nd|rd|th)?\s*(grade)?$/i, "").replace(/\s+\d+$/, "").trim();
                                      const vsMatch = opponentTeam && m.opponent && stripGrade(m.opponent) === stripGrade(opponentTeam);

                                      if (isBatterSlot) {
                                        return (
                                          <div key={matchKey} className={`flex items-center gap-2 text-[13px] leading-snug ${vsMatch ? "text-emerald-500" : "text-muted-foreground"}`}>
                                            <span className={`font-semibold tabular-nums w-[34px] text-right shrink-0 ${vsMatch ? "" : "text-foreground/80"}`}>{m.runs}{m.not_out ? "*" : ""}</span>
                                            <span className={vsMatch ? "text-emerald-500/40" : "text-muted-foreground/40"}>·</span>
                                            <span className="whitespace-nowrap">{metaParts}</span>
                                          </div>
                                        );
                                      }
                                      if (isKeeperSlot) {
                                        return (
                                          <div key={matchKey} className={`flex items-center gap-2 text-[13px] leading-snug ${vsMatch ? "text-emerald-500" : "text-muted-foreground"}`}>
                                            <span className={`font-semibold tabular-nums w-[34px] text-right shrink-0 ${vsMatch ? "" : "text-foreground/80"}`}>{m.catches || 0}/{m.stumpings || 0}</span>
                                            <span className={vsMatch ? "text-emerald-500/40" : "text-muted-foreground/40"}>·</span>
                                            <span className="whitespace-nowrap">{metaParts}</span>
                                          </div>
                                        );
                                      }
                                      if (isBowlerSlot) {
                                        return (
                                          <div key={matchKey} className={`flex items-center gap-2 text-[13px] leading-snug ${vsMatch ? "text-emerald-500" : "text-muted-foreground"}`}>
                                            <span className={`font-semibold tabular-nums w-[34px] text-right shrink-0 ${vsMatch ? "" : "text-foreground/80"}`}>{m.wickets}/{m.runs}</span>
                                            <span className={vsMatch ? "text-emerald-500/40" : "text-muted-foreground/40"}>·</span>
                                            <span className="whitespace-nowrap">{m.overs} ov · {metaParts}</span>
                                          </div>
                                        );
                                      }
                                      return null;
                                    })}
                                    {/* For keeper: also show last 5 batting beneath */}
                                    {isKeeperSlot && playerRank.last5_matches?.length > 0 && (
                                      <>
                                        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mt-2 mb-1">Last 5 Batting</div>
                                        {playerRank.last5_matches.map((bm, bi) => {
                                          const bd = bm.date ? new Date(bm.date) : null;
                                          const bDateStr = bd ? bd.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "2-digit" }) : "";
                                          const bGrade = (bm.grade || "").replace("KFC SCA ", "").replace("KFC ", "");
                                          const bRnd = bm.round ? bm.round.replace("Round ", "Rnd ").replace("Preliminary Final", "PF").replace("Grand Final", "GF").replace(/Finals Rnd (\d+)/i, "FR $1").replace(/Finals Round (\d+)/i, "FR $1") : "";
                                          const bVsMatch = (() => { const sg = (n) => (n || "").replace(/\s+\d+(st|nd|rd|th)?\s*(grade)?$/i, "").replace(/\s+\d+$/, "").trim(); return opponentTeam && bm.opponent && sg(bm.opponent) === sg(opponentTeam); })();
                                          const batKey = `bat-${bm.season || ""}-${bm.round || ""}-${bm.date || ""}-${bm.opponent || ""}-${bi}`;
                                          return (
                                            <div key={batKey} className={`flex items-center gap-2 text-[13px] leading-snug ${bVsMatch ? "text-emerald-500" : "text-muted-foreground"}`}>
                                              <span className={`font-semibold tabular-nums w-[34px] text-right shrink-0 ${bVsMatch ? "" : "text-foreground/80"}`}>{bm.runs}{bm.not_out ? "*" : ""}</span>
                                              <span className={bVsMatch ? "text-emerald-500/40" : "text-muted-foreground/40"}>·</span>
                                              <span className="whitespace-nowrap">{[(bm.season || "").replace(/^20/, ""), bGrade, bRnd, bDateStr].filter(Boolean).join(" · ")}</span>
                                            </div>
                                          );
                                        })}
                                      </>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>
                      {/* Unavailable members — shown when filter is active */}
                      {availFilter === "unavailable" && (() => {
                        const unavailRows = (availData.rows || []).filter((r) => r.status === "unavailable");
                        return (
                          <div className="mt-4 pt-3 border-t border-border/40">
                            <div className="text-xs font-semibold text-red-500/80 uppercase tracking-wider mb-2">
                              Unavailable ({unavailRows.length})
                            </div>
                            <div className="space-y-1 max-h-[200px] overflow-y-auto pr-1">
                              {unavailRows.length === 0 && (
                                <div className="text-xs text-muted-foreground text-center py-3">No members marked unavailable</div>
                              )}
                              {unavailRows.map((row) => (
                                <div key={row.user_id} className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg bg-red-500/5">
                                  {row.avatar_url ? (
                                    <img src={row.avatar_url} alt="" className="h-6 w-6 rounded-full object-cover bg-[hsl(var(--secondary))] shrink-0" />
                                  ) : (
                                    <div className="h-6 w-6 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-[9px] font-bold text-muted-foreground shrink-0">
                                      {(row.first_name?.[0] || "")}{(row.last_name?.[0] || "")}
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0 text-sm font-medium truncate text-muted-foreground">{row.first_name} {row.last_name}</div>
                                  <Badge variant="outline" className="text-[9px] font-semibold bg-red-500/10 text-red-500 border-red-500/30 shrink-0">Unavailable</Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    {/* RIGHT: Team positions */}
                    <div className="p-4">
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                        Team{isBothClub && selectedSide ? ` — ${selectedSide === "home" ? homeTeamName : awayTeamName}` : ""} (11 + 5 Emergency)
                      </div>
                      <div className="space-y-1 max-h-[420px] overflow-y-auto pr-1">
                        {posSlots.map((slotKey, idx) => {
                          const member = teamSlots[slotKey];
                          const isSlotActive = activeSlot === slotKey;
                          return (
                            <div
                              key={slotKey}
                              className={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors ${member ? "border-emerald-500/30 bg-emerald-500/5" : isSlotActive ? "border-[hsl(var(--accent))]/60 bg-[hsl(var(--accent))]/5 ring-1 ring-[hsl(var(--accent))]/30" : "border-dashed border-border/80 bg-transparent hover:bg-[hsl(var(--secondary))]/40"}`}
                              onClick={() => handleSlotClick(slotKey, idx)}
                              data-testid={`teams-slot-${slotKey}`}
                            >
                              <div className="w-5 h-5 rounded-full bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] flex items-center justify-center text-[10px] font-bold shrink-0">{idx + 1}</div>
                              {member ? (
                                <>
                                  {member.avatar_url ? (
                                    <img src={member.avatar_url} alt="" className="h-6 w-6 rounded-full object-cover bg-[hsl(var(--secondary))]" />
                                  ) : (
                                    <div className="h-6 w-6 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-[9px] font-bold text-muted-foreground">
                                      {(member.first_name?.[0] || "")}{(member.last_name?.[0] || "")}
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0 text-sm font-medium truncate">{member.first_name} {member.last_name}</div>
                                  {/* Captain / Vice Captain toggle */}
                                  <button
                                    type="button"
                                    className={`text-[9px] font-bold w-5 h-5 rounded-full flex items-center justify-center transition-colors shrink-0 ${
                                      teamRoles[slotKey] === "C"
                                        ? "bg-amber-500 text-white"
                                        : "bg-transparent text-muted-foreground/40 hover:text-amber-500 hover:bg-amber-500/10"
                                    }`}
                                    title={teamRoles[slotKey] === "C" ? "Remove Captain" : "Set as Captain"}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setTeamRoles((prev) => {
                                        const next = { ...prev };
                                        Object.keys(next).forEach((k) => { if (next[k] === "C") delete next[k]; });
                                        if (prev[slotKey] === "C") { delete next[slotKey]; } else { next[slotKey] = "C"; }
                                        return next;
                                      });
                                    }}
                                    data-testid={`teams-role-c-${slotKey}`}
                                  >C</button>
                                  <button
                                    type="button"
                                    className={`text-[8px] font-bold w-5 h-5 rounded-full flex items-center justify-center transition-colors shrink-0 ${
                                      teamRoles[slotKey] === "VC"
                                        ? "bg-blue-500 text-white"
                                        : "bg-transparent text-muted-foreground/40 hover:text-blue-500 hover:bg-blue-500/10"
                                    }`}
                                    title={teamRoles[slotKey] === "VC" ? "Remove Vice Captain" : "Set as Vice Captain"}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setTeamRoles((prev) => {
                                        const next = { ...prev };
                                        Object.keys(next).forEach((k) => { if (next[k] === "VC") delete next[k]; });
                                        if (prev[slotKey] === "VC") { delete next[slotKey]; } else { next[slotKey] = "VC"; }
                                        return next;
                                      });
                                    }}
                                    data-testid={`teams-role-vc-${slotKey}`}
                                  >VC</button>
                                  <button type="button" className="text-muted-foreground hover:text-red-500 transition-colors p-0.5" onClick={(e) => { e.stopPropagation(); removeFromSlot(slotKey); }} data-testid={`teams-slot-remove-${slotKey}`}>
                                    <XIcon className="h-3.5 w-3.5" />
                                  </button>
                                </>
                              ) : (
                                <div className="flex-1 text-xs text-muted-foreground/60 italic">{posLabel(idx)}</div>
                              )}
                            </div>
                          );
                        })}
                        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mt-3 mb-1 px-1">Emergency</div>
                        {emgSlots.map((slotKey, idx) => {
                          const member = teamSlots[slotKey];
                          return (
                            <div key={slotKey} className={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg border ${member ? "border-amber-500/30 bg-amber-500/5" : "border-dashed border-border/80 bg-transparent"}`} data-testid={`teams-slot-${slotKey}`}>
                              <div className="w-5 h-5 rounded-full bg-amber-500/10 text-amber-600 flex items-center justify-center text-[10px] font-bold shrink-0">E{idx + 1}</div>
                              {member ? (
                                <>
                                  {member.avatar_url ? (
                                    <img src={member.avatar_url} alt="" className="h-6 w-6 rounded-full object-cover bg-[hsl(var(--secondary))]" />
                                  ) : (
                                    <div className="h-6 w-6 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-[9px] font-bold text-muted-foreground">
                                      {(member.first_name?.[0] || "")}{(member.last_name?.[0] || "")}
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0 text-sm font-medium truncate">{member.first_name} {member.last_name}</div>
                                  <button type="button" className="text-muted-foreground hover:text-red-500 transition-colors p-0.5" onClick={() => removeFromSlot(slotKey)} data-testid={`teams-slot-remove-${slotKey}`}>
                                    <XIcon className="h-3.5 w-3.5" />
                                  </button>
                                </>
                              ) : (
                                <div className="flex-1 text-xs text-muted-foreground/60 italic">Emergency {idx + 1}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {/* Save / Publish actions */}
                      <div className="mt-4 pt-3 border-t border-border/40 space-y-3">
                        <label className="flex items-center gap-2 cursor-pointer" data-testid="notify-non-selected-toggle">
                          <Switch
                            checked={notifyNonSelected}
                            onCheckedChange={setNotifyNonSelected}
                            className="scale-90"
                          />
                          <span className="text-xs text-muted-foreground leading-tight">
                            Notify available players who weren't selected
                          </span>
                        </label>
                        <div>
                          <button type="button" onClick={() => {
                            const opening = !enterPlayerOpen;
                            setEnterPlayerOpen(opening);
                            if (!opening) { setEnterPlayerSearch(""); setSearchResults([]); }
                          }} className={`w-full h-11 sm:h-9 rounded-lg text-xs font-medium border transition-colors inline-flex items-center justify-center gap-1.5 ${enterPlayerOpen ? "border-[#1a3a7a]/40 bg-[#1a3a7a]/15 text-[#1a3a7a]" : "border-[#1a3a7a]/30 bg-[#1a3a7a]/10 text-[#1a3a7a] hover:bg-[#1a3a7a]/20"}`} data-testid="teams-enter-player-btn">
                            <UserPlus className="w-3.5 h-3.5" />
                            {enterPlayerOpen ? "Close Player Search" : "Enter Player"}
                          </button>
                          {enterPlayerOpen && (() => {
                            const assignedIds = new Set(Object.values(teamSlots).filter(Boolean).map((m) => m.user_id));
                            const availableOrReserveIds = new Set((availData?.rows || []).filter((r) => r.status === "available" || r.status === "reserve").map((r) => r.user_id));
                            const q = enterPlayerSearch.trim();
                            const filtered = searchResults.filter((r) => !assignedIds.has(r.id) && !availableOrReserveIds.has(r.id));
                            // Check if typed name has no matches → allow guest creation
                            const canAddGuest = q.length >= 2 && !searchLoading && filtered.length === 0;
                            const nameParts = q.split(" ");
                            const guestFirst = nameParts[0] || "";
                            const guestLast = nameParts.slice(1).join(" ") || "";

                            return (
                              <div className="mt-2 border border-border/60 rounded-lg bg-[hsl(var(--card))] overflow-hidden" data-testid="enter-player-panel">
                                <div className="p-2">
                                  <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                                    <Input
                                      value={enterPlayerSearch}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setEnterPlayerSearch(val);
                                        clearTimeout(searchTimer.current);
                                        const trimmed = val.trim();
                                        if (trimmed.length < 2) { setSearchResults([]); setSearchLoading(false); return; }
                                        setSearchLoading(true);
                                        searchTimer.current = setTimeout(async () => {
                                          try {
                                            const { data } = await api.get(`/teams/search-players?q=${encodeURIComponent(trimmed)}`);
                                            setSearchResults(data.results || []);
                                          } catch { setSearchResults([]); }
                                          setSearchLoading(false);
                                        }, 350);
                                      }}
                                      placeholder="Search members & players…"
                                      className="h-8 pl-8 text-xs"
                                      autoFocus
                                      data-testid="enter-player-search"
                                    />
                                  </div>
                                </div>
                                <div className="max-h-56 overflow-y-auto border-t border-border/40">
                                  {searchLoading ? (
                                    <div className="flex items-center justify-center py-6 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /></div>
                                  ) : q.length < 2 ? (
                                    <div className="py-4 text-center text-xs text-muted-foreground">Type a name to search</div>
                                  ) : filtered.length === 0 && !canAddGuest ? (
                                    <div className="py-4 text-center text-xs text-muted-foreground">No matching players</div>
                                  ) : (
                                    <>
                                      {filtered.map((r) => {
                                        const sourceBadge = r.source === "member"
                                          ? <Badge variant="outline" className="text-[9px] font-semibold bg-blue-500/10 text-blue-600 border-blue-500/30 shrink-0 py-0 px-1.5">Member</Badge>
                                          : r.source === "playhq"
                                          ? <Badge variant="outline" className="text-[9px] font-semibold bg-violet-500/10 text-violet-600 border-violet-500/30 shrink-0 py-0 px-1.5">PlayHQ</Badge>
                                          : <Badge variant="outline" className="text-[9px] font-semibold bg-amber-500/10 text-amber-600 border-amber-500/30 shrink-0 py-0 px-1.5">Guest</Badge>;
                                        const addPlayer = () => {
                                          const row = {
                                            user_id: r.id,
                                            first_name: r.first_name,
                                            last_name: r.last_name,
                                            avatar_url: r.avatar_url || null,
                                            status: "available",
                                            source: r.source,
                                            player_key: r.player_key || null,
                                            is_guest: r.source === "guest" || r.source === "playhq",
                                          };
                                          setAvailData((prev) => {
                                            if (!prev) return { rows: [row], counts: { available: 1, reserve: 0, unavailable: 0 } };
                                            const exists = prev.rows.findIndex((x) => x.user_id === r.id);
                                            const newRows = [...prev.rows];
                                            if (exists >= 0) { newRows[exists] = row; } else { newRows.push(row); }
                                            const counts = { available: 0, reserve: 0, unavailable: 0 };
                                            newRows.forEach((x) => { if (counts[x.status] !== undefined) counts[x.status]++; });
                                            return { ...prev, rows: newRows, counts };
                                          });
                                        };
                                        return (
                                          <div key={`${r.source}-${r.id}`} className="flex items-center gap-2.5 px-3 py-2 hover:bg-[hsl(var(--secondary))]/60 transition-colors cursor-pointer" onClick={addPlayer} data-testid={`enter-player-row-${r.id}`}>
                                            {r.avatar_url ? (
                                              <img src={r.avatar_url} alt="" className="h-7 w-7 rounded-full object-cover bg-[hsl(var(--secondary))] shrink-0" />
                                            ) : (
                                              <div className="h-7 w-7 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-[10px] font-bold text-muted-foreground shrink-0">
                                                {(r.first_name?.[0] || "").toUpperCase()}{(r.last_name?.[0] || "").toUpperCase()}
                                              </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                              <div className="text-sm font-medium truncate flex items-center gap-1.5">
                                                {r.name}
                                                {sourceBadge}
                                              </div>
                                              {r.member_id && <div className="text-[10px] text-muted-foreground">ID: {r.member_id}</div>}
                                            </div>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="h-8 px-3 text-xs font-semibold shrink-0 border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/10"
                                              onClick={(e) => { e.stopPropagation(); addPlayer(); }}
                                              data-testid={`enter-player-assign-${r.id}`}
                                            >+ Add</Button>
                                          </div>
                                        );
                                      })}
                                      {canAddGuest && (
                                        <div className="flex items-center gap-2.5 px-3 py-2.5 border-t border-border/40 bg-amber-500/5" data-testid="enter-player-add-guest">
                                          <div className="h-7 w-7 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
                                            <UserPlus className="w-3.5 h-3.5 text-amber-600" />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">Add "{q}" as guest</div>
                                            <div className="text-[10px] text-muted-foreground">Stats will be tracked</div>
                                          </div>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-7 px-2 text-[10px] shrink-0"
                                            onClick={async () => {
                                              try {
                                                const { data } = await api.post("/teams/guest-players", { first_name: guestFirst, last_name: guestLast });
                                                const row = {
                                                  user_id: data.id,
                                                  first_name: data.first_name,
                                                  last_name: data.last_name,
                                                  avatar_url: null,
                                                  status: "available",
                                                  source: "guest",
                                                  player_key: data.player_key,
                                                  is_guest: true,
                                                };
                                                setAvailData((prev) => {
                                                  if (!prev) return { rows: [row], counts: { available: 1, reserve: 0, unavailable: 0 } };
                                                  const newRows = [...prev.rows, row];
                                                  const counts = { available: 0, reserve: 0, unavailable: 0 };
                                                  newRows.forEach((x) => { if (counts[x.status] !== undefined) counts[x.status]++; });
                                                  return { ...prev, rows: newRows, counts };
                                                });
                                                setEnterPlayerSearch("");
                                                setSearchResults([]);
                                                toast.success(`${data.name} added as guest`);
                                              } catch { toast.error("Failed to create guest player"); }
                                            }}
                                            data-testid="enter-player-create-guest-btn"
                                          >+ Add</Button>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <button type="button" onClick={handleSaveTeam} disabled={saving} className="flex-1 h-11 sm:h-9 rounded-lg text-xs font-medium border border-border bg-[hsl(var(--secondary))]/60 text-foreground hover:bg-[hsl(var(--secondary))] transition-colors disabled:opacity-50" data-testid="teams-save-btn">{saving ? "Saving…" : "Save Team"}</button>
                          <button type="button" onClick={() => {
                            const links = selectionLinks || {};
                            const lookupName = isBothClub ? effectiveClubTeamName : clubTeamName;
                            const url = links[gradeName] || links[lookupName];
                            if (url) { window.open(url, "_blank", "noopener,noreferrer"); onEnteredPlayHQ(); } else { toast.error("No PlayHQ link set for " + (lookupName || gradeName || "this grade") + ". Add one in PlayHQ → Team Selection Links."); }
                          }} className={`flex-1 h-11 sm:h-9 rounded-lg text-xs font-medium border transition-colors inline-flex items-center justify-center gap-1.5 ${enteredPlayHQ ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700" : "border-[#1a3a7a]/30 bg-[#1a3a7a]/10 text-[#1a3a7a] hover:bg-[#1a3a7a]/20"}`} data-testid="teams-enter-playhq-btn">{enteredPlayHQ && <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />}{enteredPlayHQ ? "Entered in PlayHQ" : "Enter team in PlayHQ"}</button>
                          {isPublished ? (
                            <button type="button" onClick={() => setUnpublishConfirmOpen(true)} disabled={unpublishing} className="flex-1 h-11 sm:h-9 rounded-lg text-xs font-medium border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-1.5" data-testid="teams-unpublish-btn">
                              <XCircle className="h-3.5 w-3.5" />
                              {unpublishing ? "Unpublishing…" : "Unpublish Team"}
                            </button>
                          ) : (
                            <button type="button" onClick={handlePublishTeam} disabled={publishing} className="flex-1 h-11 sm:h-9 rounded-lg text-xs font-medium bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90 transition-opacity disabled:opacity-50" data-testid="teams-publish-btn">{publishing ? "Publishing…" : "Publish Team"}</button>
                          )}
                        </div>
                        {/* Unpublish confirmation dialog */}
                        <AlertDialog open={unpublishConfirmOpen} onOpenChange={setUnpublishConfirmOpen}>
                          <AlertDialogContent data-testid="unpublish-confirm-dialog">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Unpublish Team Selection?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will retract the published team list and remove the flyer from News. Players will no longer see the team selection notification.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid="unpublish-cancel-btn">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={handleUnpublishTeam}
                                disabled={unpublishing}
                                className="bg-red-600 text-white hover:bg-red-700"
                                data-testid="unpublish-confirm-btn"
                              >
                                {unpublishing ? "Unpublishing…" : "Yes, Unpublish"}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Non-activated fixture — expand on click to show activate button */}
      {isSelected && !isActivated && (
        <div
          className="border-t border-border/60 px-4 py-4 bg-[hsl(var(--secondary))]/30"
          data-testid={`teams-phq-activation-panel-${game.playhq_id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-semibold text-foreground">Team Availability</div>
              <div className="text-xs text-muted-foreground">
                Activate to let members respond with their availability for this match.
              </div>
            </div>
            <Button
              size="sm"
              className="btn-primary-modern text-xs"
              onClick={() => onActivate()}
              disabled={activating}
              data-testid={`teams-phq-activate-${game.playhq_id}`}
            >
              {activating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
              Activate Availability
            </Button>
          </div>
        </div>
      )}
      {/* Push Delivery Log Dialog */}
      {deliveryLog && (
        <Dialog open={!!deliveryLog} onOpenChange={(o) => { if (!o) setDeliveryLog(null); }}>
          <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="delivery-log-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Team Published
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Summary badges */}
              <div className="flex flex-wrap gap-2" data-testid="delivery-summary">
                <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30 gap-1">
                  <Bell className="h-3 w-3" />
                  {deliveryLog.summary?.push_sent || 0} push sent
                </Badge>
                {(deliveryLog.summary?.no_subscription || 0) > 0 && (
                  <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30 gap-1">
                    <BellOff className="h-3 w-3" />
                    {deliveryLog.summary.no_subscription} no subscription
                  </Badge>
                )}
                {(deliveryLog.summary?.push_failed || 0) > 0 && (
                  <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/30 gap-1">
                    <XCircle className="h-3 w-3" />
                    {deliveryLog.summary.push_failed} push failed
                  </Badge>
                )}
                {(deliveryLog.summary?.skipped_preview || 0) > 0 && (
                  <Badge variant="outline" className="bg-muted text-muted-foreground border-border gap-1">
                    <BellOff className="h-3 w-3" />
                    {deliveryLog.summary.skipped_preview} skipped (preview)
                  </Badge>
                )}
                {(deliveryLog.summary?.non_selected_notified || 0) > 0 && (
                  <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/30 gap-1">
                    <Bell className="h-3 w-3" />
                    {deliveryLog.summary.non_selected_notified} non-selected notified
                  </Badge>
                )}
                <Badge variant="outline" className="bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] border-[hsl(var(--accent))]/30 gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  {deliveryLog.summary?.total || 0} in-app
                </Badge>
              </div>

              {/* Per-player table */}
              <div className="rounded-xl border border-border overflow-hidden">
                <table className="w-full text-sm" data-testid="delivery-log-table">
                  <thead className="bg-[hsl(var(--secondary))]/60">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Player</th>
                      <th className="text-center px-3 py-2 font-medium text-muted-foreground w-24">In-App</th>
                      <th className="text-center px-3 py-2 font-medium text-muted-foreground w-24">Push</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {(deliveryLog.delivery || []).map((p) => (
                      <tr key={p.user_id} className="hover:bg-[hsl(var(--secondary))]/30 transition-colors">
                        <td className="px-3 py-2 font-medium">{p.name}</td>
                        <td className="px-3 py-2 text-center">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                        </td>
                        <td className="px-3 py-2 text-center">
                          {p.push === "sent" && <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />}
                          {p.push === "failed" && (
                            <span title="Push delivery failed" className="inline-flex items-center gap-1 text-xs text-red-500">
                              <XCircle className="h-4 w-4" />
                            </span>
                          )}
                          {p.push === "no_subscription" && (
                            <span title="No push subscription registered" className="inline-flex items-center gap-1 text-xs text-amber-500">
                              <BellOff className="h-4 w-4" />
                            </span>
                          )}
                          {p.push === "skipped_preview" && (
                            <span title="Skipped — preview mode" className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                              <BellOff className="h-4 w-4" />
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={() => setDeliveryLog(null)}
                  className="rounded-full"
                  data-testid="delivery-log-close"
                >
                  Done
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

    </li>
  );
}

function PhqFixtureTeam({ teamRef, teamMap, clubTeamIds, align = "left" }) {
  const team = teamMap[teamRef?.id];
  const name = team?.name || "TBC";
  const logo = pickLogo(team?.raw);
  const isClub = clubTeamIds.has(teamRef?.id);
  const nameCls = `text-sm sm:text-base truncate ${isClub ? "font-semibold" : "font-medium"} text-foreground/85`;
  const LogoEl = logo ? (
    <img
      src={logo}
      alt=""
      className="h-9 w-9 sm:h-10 sm:w-10 rounded-full object-cover shrink-0 bg-[hsl(var(--secondary))]"
      loading="lazy"
    />
  ) : (
    <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-[hsl(var(--secondary))] shrink-0" />
  );
  const nameEl = <div className={`${nameCls} ${align === "right" ? "text-right" : ""}`}>{name}</div>;
  return (
    <div className={`flex items-center gap-2 sm:gap-3 min-w-0 ${align === "right" ? "flex-row justify-end" : "flex-row justify-start"}`}>
      {align === "right" && nameEl}
      {LogoEl}
      {align === "left" && nameEl}
    </div>
  );
}

function PhqGradeChip({ label, count, active, onClick, testId, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      role="tab"
      aria-selected={active}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))]/40 ${
        active
          ? "bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-white"
          : "bg-[hsl(var(--card))] border-border text-foreground/80 hover:border-foreground/40 hover:text-foreground"
      }`}
    >
      <span className="truncate max-w-[10rem]">{label}</span>
      {typeof count === "number" && (
        <span
          className={`min-w-[1.25rem] h-5 px-1.5 inline-flex items-center justify-center rounded-full text-[10px] font-bold tabular-nums ${
            active ? "bg-white/30 text-white" : "bg-foreground/10 text-foreground border border-border/60"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}
